import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Droplets, Wind, Plus, X, Settings, Trash2, Pencil, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

// Field groups keep the large water / HVAC forms declarative & maintainable.
const WATER_GROUPS = [
  { title: "Test", fields: [
    ["test_scenario", "Test scenario", "text"], ["outlet", "Outlet / fixture", "text"],
    ["simultaneous_outlets", "Simultaneous outlets in use", "number"], ["simultaneous_bathrooms", "Bathrooms in use", "number"],
  ]},
  { title: "Booster pump", fields: [
    ["booster_pump_id", "Booster pump ID / model", "text"], ["booster_pump_status", "Status", "text"],
    ["booster_suction_pressure_bar", "Suction pressure (bar)", "number"], ["booster_discharge_pressure_bar", "Discharge pressure (bar)", "number"],
    ["booster_set_point_bar", "Set point (bar)", "number"],
  ]},
  { title: "Recirculation & heater", fields: [
    ["recirc_pump_id", "Recirc pump ID", "text"], ["recirc_pump_status", "Recirc status", "text"], ["recirc_return_temp_c", "Return temp (°C)", "number"],
    ["water_heater_id", "Water heater ID", "text"], ["water_heater_status", "Heater status", "text"], ["water_heater_set_temp_c", "Heater set temp (°C)", "number"],
  ]},
  { title: "Pressures & flow", fields: [
    ["cold_static_pressure_bar", "Cold static (bar)", "number"], ["hot_static_pressure_bar", "Hot static (bar)", "number"],
    ["cold_dynamic_residual_pressure_bar", "Cold dynamic residual (bar)", "number"], ["hot_dynamic_residual_pressure_bar", "Hot dynamic residual (bar)", "number"],
    ["cold_flow_lpm", "Cold flow (L/min)", "number"], ["hot_flow_lpm", "Hot flow (L/min)", "number"],
  ]},
  { title: "Pressure drop & hot water", fields: [
    ["before_pressure_bar", "Before test (bar)", "number"], ["during_pressure_bar", "During test (bar)", "number"], ["pressure_drop_bar", "Calculated pressure drop (bar)", "number"],
    ["hot_water_delivered_temp_c", "Hot water delivered (°C)", "number"], ["time_to_temp_s", "Time to temperature (s)", "number"],
    ["simultaneous_pressure_drop_bar", "Simultaneous-demand pressure drop (bar)", "number"],
  ]},
];

const HVAC_GROUPS = [
  { title: "Equipment", fields: [
    ["fcu_id", "FCU ID", "text"], ["ahu_id", "AHU ID", "text"], ["duct_id", "Duct ID", "text"],
    ["thermostat_id", "Thermostat ID", "text"], ["dehumidifier_id", "Dehumidifier ID", "text"], ["hepa_id", "HEPA / filter ID", "text"],
    ["operating_status", "Operating status", "text"],
  ]},
  { title: "Temperature & humidity", fields: [
    ["supply_temp_c", "Supply temp (°C)", "number"], ["return_temp_c", "Return temp (°C)", "number"], ["room_temp_c", "Room temp (°C)", "number"],
    ["relative_humidity_pct", "Relative humidity (%)", "number"], ["rh_target_min", "RH target min (%)", "number"], ["rh_target_max", "RH target max (%)", "number"],
  ]},
  { title: "Filter & airflow", fields: [
    ["filter_diff_pressure_pa", "Filter differential pressure (Pa)", "number"], ["filter_condition", "Filter condition", "text"],
    ["airflow_cmh", "Airflow (m³/h)", "number"], ["ach", "Air changes / hour", "number"],
    ["last_service_date", "Last service date", "date"], ["last_filter_service_date", "Last filter change date", "date"],
  ]},
];

const ResultBadge = ({ value }) => {
  const map = {
    pass: ["bg-[#3F6E2C]/12 text-[#2E5220]", CheckCircle2, "Pass"],
    fail: ["bg-[#A3141A]/12 text-[#A3141A]", AlertTriangle, "Fail"],
    unevaluated: ["bg-[#7A6B5F]/12 text-[#7A6B5F]", HelpCircle, "Not evaluated"],
  };
  const [cls, Icon, label] = map[value] || map.unevaluated;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide ${cls}`} data-testid={`commissioning-result-${value}`}>
      <Icon size={11} /> {label}
    </span>
  );
};

function SummaryCards({ summary }) {
  const cards = [
    { k: "water", label: "Water flow", icon: Droplets, s: summary.water },
    { k: "hvac", label: "HVAC / Environmental", icon: Wind, s: summary.hvac },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="commissioning-summary">
      {cards.map(({ k, label, icon: Icon, s }) => (
        <div key={k} className="border border-[#6E1F2C]/15 bg-white p-4" data-testid={`commissioning-summary-${k}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={15} className="text-[#6E1F2C]" />
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">{label}</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#1C110C]">
            <span>Total <b>{s.total}</b></span>
            <span className="text-[#2E5220]">Pass <b>{s.pass}</b></span>
            <span className="text-[#A3141A]">Fail <b>{s.fail}</b></span>
            {s.unevaluated > 0 && <span className="text-[#7A6B5F]">Unevaluated <b>{s.unevaluated}</b></span>}
            {s.overdue_filters > 0 && <span className="text-[#A6822F]">Overdue filters <b>{s.overdue_filters}</b></span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function RecordModal({ slug, type, record, onClose, onSaved }) {
  const groups = type === "water" ? WATER_GROUPS : HVAC_GROUPS;
  const [form, setForm] = useState(() => ({
    test_datetime: record?.test_datetime || "",
    zone_area: record?.zone_area || "",
    tester_name: record?.tester_name || "",
    witness_name: record?.witness_name || "",
    witness_role: record?.witness_role || "",
    corrective_action: record?.corrective_action || "",
    notes: record?.notes || "",
    signed_off: !!record?.signed_off,
    data: { ...(record?.data || {}) },
    attachments: record?.attachments || [],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setData = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const res = await portalApi.upload(slug, files, "document");
      const list = res.attachments || res.items || (Array.isArray(res) ? res : []);
      const refs = list.map((a) => ({ id: a.id, name: a.filename || a.name || "file", url: portalApi.attachmentUrl(a.id) }));
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...refs] }));
    } catch (ex) { setErr(ex.message); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { type, ...form,
        signed_off_at: form.signed_off ? new Date().toISOString() : null };
      const res = record
        ? await portalApi.updateCommissioning(slug, record.id, payload)
        : await portalApi.createCommissioning(slug, payload);
      onSaved(res.record);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="commissioning-modal">
      <div className="bg-[#FAF5EC] w-full max-w-3xl border border-[#6E1F2C]/20">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#6E1F2C]/15 sticky top-0 bg-[#FAF5EC]">
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C]">
            {record ? "Edit" : "New"} {type === "water" ? "water flow" : "HVAC"} record
          </p>
          <button onClick={onClose} data-testid="commissioning-modal-close" className="text-[#6E1F2C]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Test date / time" type="datetime-local" value={form.test_datetime} onChange={(v) => setForm((f) => ({ ...f, test_datetime: v }))} testid="commissioning-field-test_datetime" />
            <Field label={type === "water" ? "Bathroom / area" : "Zone / room"} value={form.zone_area} onChange={(v) => setForm((f) => ({ ...f, zone_area: v }))} testid="commissioning-field-zone" />
            <Field label="Tester" value={form.tester_name} onChange={(v) => setForm((f) => ({ ...f, tester_name: v }))} testid="commissioning-field-tester" />
          </div>
          {groups.map((g) => (
            <div key={g.title}>
              <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F] mb-2">{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {g.fields.map(([k, label, ftype]) => (
                  <Field key={k} label={label} type={ftype} value={form.data[k] ?? ""} onChange={(v) => setData(k, v)} testid={`commissioning-data-${k}`} />
                ))}
              </div>
            </div>
          ))}
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F] mb-2">Result & sign-off</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Corrective action" value={form.corrective_action} onChange={(v) => setForm((f) => ({ ...f, corrective_action: v }))} testid="commissioning-field-corrective" />
              <Field label="Witness / consultant" value={form.witness_name} onChange={(v) => setForm((f) => ({ ...f, witness_name: v }))} testid="commissioning-field-witness" />
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs text-[#1C110C]">
              <input type="checkbox" checked={form.signed_off} onChange={(e) => setForm((f) => ({ ...f, signed_off: e.target.checked }))} data-testid="commissioning-field-signoff" />
              Witnessed &amp; signed off
            </label>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F] mb-2">Attachments / photos</p>
            <input type="file" multiple onChange={uploadFiles} data-testid="commissioning-field-attachments" className="text-xs" />
            {form.attachments.length > 0 && (
              <ul className="mt-2 text-xs text-[#6E1F2C] list-disc pl-5">
                {form.attachments.map((a, i) => <li key={i}>{a.name}</li>)}
              </ul>
            )}
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} testid="commissioning-field-notes" />
          {err && <p className="text-xs text-[#A3141A]" data-testid="commissioning-modal-error">{err}</p>}
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-[#6E1F2C]/15">
          <button onClick={onClose} className="flex-1 border border-[#6E1F2C]/30 text-[#6E1F2C] px-5 py-3 text-[11px] tracking-[0.28em] uppercase">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="commissioning-modal-save" className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-3 text-[11px] tracking-[0.28em] uppercase disabled:opacity-50">
            {busy ? "Saving…" : "Save record"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">{label}</label>
    <input
      type={type} value={value} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
    />
  </div>
);

function Section({ type, records, canEdit, onAdd, onEdit, onDelete }) {
  const Icon = type === "water" ? Droplets : Wind;
  return (
    <div className="border border-[#6E1F2C]/15 bg-white" data-testid={`commissioning-section-${type}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#6E1F2C]/10">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-[#6E1F2C]" />
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">{type === "water" ? "Water flow" : "HVAC / Environmental"}</p>
        </div>
        {canEdit && (
          <button onClick={() => onAdd(type)} data-testid={`commissioning-add-${type}`} className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase bg-[#6E1F2C] text-white px-3 py-1.5">
            <Plus size={12} /> Add
          </button>
        )}
      </div>
      {records.length === 0 ? (
        <p className="px-4 py-4 text-xs text-[#7A6B5F]">No records yet.</p>
      ) : (
        <div className="divide-y divide-[#6E1F2C]/10">
          {records.map((r) => (
            <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 justify-between" data-testid={`commissioning-row-${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-[#1C110C]">
                  {r.zone_area || "—"}{" "}
                  <ResultBadge value={r.result} />
                  {r.overdue_filter && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#A6822F]">Filter overdue</span>}
                  {r.simultaneous_unacceptable && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#A3141A]">Sim. loss</span>}
                  {r.signed_off && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#2E5220]">Signed</span>}
                </p>
                <p className="text-[10px] text-[#7A6B5F] mt-0.5">
                  {(r.test_datetime || "").replace("T", " ")} · {r.tester_name || "—"}
                  {(r.out_of_range || []).length > 0 && <span className="text-[#A3141A]"> · Out of range: {r.out_of_range.join(", ")}</span>}
                </p>
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <button onClick={() => onEdit(r)} data-testid={`commissioning-edit-${r.id}`} className="text-[#6E1F2C] p-1.5 hover:bg-[#6E1F2C]/10"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(r)} data-testid={`commissioning-delete-${r.id}`} className="text-[#A3141A] p-1.5 hover:bg-[#A3141A]/10"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfigModal({ slug, config, onClose, onSaved }) {
  const [water, setWater] = useState(config.water || {});
  const [hvac, setHvac] = useState(config.hvac || {});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const waterFields = [
    ["min_dynamic_residual_pressure_bar", "Min dynamic residual pressure (bar)"],
    ["max_pressure_drop_bar", "Max acceptable pressure drop (bar)"],
    ["min_flow_lpm", "Min flow (L/min)"], ["hot_water_min_temp_c", "Min hot water temp (°C)"],
    ["hot_water_max_time_to_temp_s", "Max time to temperature (s)"],
  ];
  const hvacFields = [
    ["rh_min", "RH min (%)"], ["rh_max", "RH max (%)"], ["temp_min_c", "Temp min (°C)"], ["temp_max_c", "Temp max (°C)"],
    ["max_filter_diff_pressure_pa", "Max filter differential pressure (Pa)"], ["filter_service_interval_days", "Filter service interval (days)"],
  ];
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const clean = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, num(v)]).filter(([, v]) => v !== undefined && !Number.isNaN(v)));
      const res = await portalApi.setCommissioningConfig(slug, { water: clean(water), hvac: clean(hvac) });
      onSaved(res.config);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="commissioning-config-modal">
      <div className="bg-[#FAF5EC] w-full max-w-2xl border border-[#6E1F2C]/20">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#6E1F2C]/15">
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C]">Acceptance criteria (per project)</p>
          <button onClick={onClose} className="text-[#6E1F2C]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-[#7A6B5F]">Set only the limits approved in the design / commissioning criteria. Metrics left blank are not auto-evaluated (pass/fail is only computed against configured limits).</p>
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F] mb-2">Water flow</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {waterFields.map(([k, l]) => <Field key={k} label={l} type="number" value={water[k] ?? ""} onChange={(v) => setWater((c) => ({ ...c, [k]: v }))} testid={`commissioning-config-water-${k}`} />)}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F] mb-2">HVAC / Environmental</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {hvacFields.map(([k, l]) => <Field key={k} label={l} type="number" value={hvac[k] ?? ""} onChange={(v) => setHvac((c) => ({ ...c, [k]: v }))} testid={`commissioning-config-hvac-${k}`} />)}
            </div>
          </div>
          {err && <p className="text-xs text-[#A3141A]">{err}</p>}
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-[#6E1F2C]/15">
          <button onClick={onClose} className="flex-1 border border-[#6E1F2C]/30 text-[#6E1F2C] px-5 py-3 text-[11px] tracking-[0.28em] uppercase">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="commissioning-config-save" className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-3 text-[11px] tracking-[0.28em] uppercase disabled:opacity-50">{busy ? "Saving…" : "Save criteria"}</button>
        </div>
      </div>
    </div>
  );
}

export default function CommissioningTab({ slug }) {
  const [state, setState] = useState(null);
  const [filters, setFilters] = useState({ result: "", zone: "" });
  const [modal, setModal] = useState(null); // {type, record}
  const [configOpen, setConfigOpen] = useState(false);

  const load = useCallback(async () => {
    const d = await portalApi.commissioning(slug, filters);
    setState(d);
  }, [slug, filters]);
  useEffect(() => { load(); }, [load]);

  const canEdit = state?.can_edit;
  const water = useMemo(() => (state?.records || []).filter((r) => r.type === "water"), [state]);
  const hvac = useMemo(() => (state?.records || []).filter((r) => r.type === "hvac"), [state]);

  const remove = async (r) => {
    if (!window.confirm("Delete this commissioning record?")) return;
    await portalApi.deleteCommissioning(slug, r.id);
    load();
  };

  if (!state) return <div className="text-sm text-[#7A6B5F] py-8" data-testid="commissioning-loading">Loading commissioning…</div>;

  return (
    <div className="space-y-4" data-testid="commissioning-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SummaryCards summary={state.summary} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={filters.result} onChange={(e) => setFilters((f) => ({ ...f, result: e.target.value }))} data-testid="commissioning-filter-result" className="bg-white border border-[#6E1F2C]/20 px-3 py-2 text-xs">
          <option value="">All results</option>
          <option value="pass">Pass</option>
          <option value="fail">Fail</option>
          <option value="unevaluated">Not evaluated</option>
        </select>
        <input placeholder="Filter by zone / area…" value={filters.zone} onChange={(e) => setFilters((f) => ({ ...f, zone: e.target.value }))} data-testid="commissioning-filter-zone" className="bg-white border border-[#6E1F2C]/20 px-3 py-2 text-xs flex-1 min-w-[180px]" />
        {canEdit && (
          <button onClick={() => setConfigOpen(true)} data-testid="commissioning-config-btn" className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase border border-[#6E1F2C]/30 text-[#6E1F2C] px-3 py-2">
            <Settings size={13} /> Acceptance criteria
          </button>
        )}
      </div>

      <Section type="water" records={water} canEdit={canEdit} onAdd={(t) => setModal({ type: t })} onEdit={(r) => setModal({ type: r.type, record: r })} onDelete={remove} />
      <Section type="hvac" records={hvac} canEdit={canEdit} onAdd={(t) => setModal({ type: t })} onEdit={(r) => setModal({ type: r.type, record: r })} onDelete={remove} />

      {modal && (
        <RecordModal slug={slug} type={modal.type} record={modal.record} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {configOpen && (
        <ConfigModal slug={slug} config={state.config} onClose={() => setConfigOpen(false)} onSaved={() => { setConfigOpen(false); load(); }} />
      )}
    </div>
  );
}
